import { computed, inject, Service, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { DEFAULT_LOCALE, normalizeLocale } from '../i18n/locales';

type MessageParameters = Readonly<Record<string, number | string>>;

@Service()
export class TranslationService {
  private readonly translations = inject(TranslateService);
  readonly locale = computed(() => normalizeLocale(this.translations.currentLang()));

  private readonly context = signal<Readonly<{ error: null | string; revision: string }>>({
    revision: 'development',
    error: null,
  });
  readonly catalogueRevision = computed(() => this.context().revision);
  readonly loadError = computed(() => this.context().error);
  readonly loading = this.translations.isLoading;

  async setContext(rawLocale: null | string, rawRevision: null | string): Promise<void> {
    const revision = rawRevision?.trim() ?? '';
    const context = { revision: revision.length > 0 ? revision : 'development', error: null };
    this.context.set(context);

    try {
      await firstValueFrom(this.translations.use(normalizeLocale(rawLocale)));
    } catch (error: unknown) {
      if (this.context() !== context) {
        return;
      }

      const message = error instanceof Error ? error.message : String(error);
      console.error('[IMA i18n] Catalogue loading failed:', error);
      this.context.set({ ...context, error: message });
      await firstValueFrom(this.translations.use(DEFAULT_LOCALE));
    }
  }

  t(key: string, parameters: MessageParameters = {}): string {
    const message: unknown = this.translations.instant(key, parameters);

    if (typeof message !== 'string') {
      // eslint-disable-next-line functional/no-throw-statements -- Invalid catalogue values are contract errors, not display strings.
      throw new TypeError(`Translation "${key}" must resolve to a string.`);
    }

    return message;
  }
}
